'use client';

import { c as _c } from "react/compiler-runtime";
import { NavArrowLeft, NavArrowRight } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { useContext } from 'react';
import { Calendar as AriaCalendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, CalendarStateContext, type DateValue, Heading, Text, useLocale } from 'react-aria-components';
import { Button } from '~/components/Button';
import styles from './Calendar.module.css';
import type { CalendarProps } from './Calendar.types';
function MonthDropdown() {
  const $ = _c(10);
  const {
    locale
  } = useLocale();
  const state = useContext(CalendarStateContext);
  let t0;
  if ($[0] !== locale) {
    t0 = Array.from({
      length: 12
    }, (_, i) => ({
      value: i + 1,
      label: new Intl.DateTimeFormat(locale, {
        month: "long"
      }).format(new Date(2000, i, 1))
    }));
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const months = t0;
  if (!state) {
    return null;
  }
  const t1 = state.focusedDate.month;
  let t2;
  if ($[2] !== state) {
    t2 = e => state.setFocusedDate(state.focusedDate.set({
      month: Number(e.target.value)
    }));
    $[2] = state;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== months) {
    t3 = months.map(_temp);
    $[4] = months;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== state.focusedDate.month || $[7] !== t2 || $[8] !== t3) {
    t4 = <select aria-label="Month" className={styles.dropdownSelect} value={t1} onChange={t2}>{t3}</select>;
    $[6] = state.focusedDate.month;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp(m) {
  return <option key={m.value} value={m.value}>{m.label}</option>;
}
function YearDropdown() {
  const $ = _c(13);
  const state = useContext(CalendarStateContext);
  if (!state) {
    return null;
  }
  const currentYear = state.focusedDate.year;
  const minYear = state.minValue ? state.minValue.year : currentYear - 10;
  const maxYear = state.maxValue ? state.maxValue.year : currentYear + 5;
  let t0;
  if ($[0] !== maxYear || $[1] !== minYear) {
    let t1;
    if ($[3] !== minYear) {
      t1 = (_, i) => minYear + i;
      $[3] = minYear;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = Array.from({
      length: maxYear - minYear + 1
    }, t1);
    $[0] = maxYear;
    $[1] = minYear;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const years = t0;
  let t1;
  if ($[5] !== state) {
    t1 = e => state.setFocusedDate(state.focusedDate.set({
      year: Number(e.target.value)
    }));
    $[5] = state;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== years) {
    t2 = years.map(_temp2);
    $[7] = years;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== currentYear || $[10] !== t1 || $[11] !== t2) {
    t3 = <select aria-label="Year" className={styles.dropdownSelect} value={currentYear} onChange={t1}>{t2}</select>;
    $[9] = currentYear;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function _temp2(y) {
  return <option key={y} value={y}>{y}</option>;
}
const Calendar = t0 => {
  const $ = _c(27);
  let errorMessage;
  let props;
  let showMonthYearDropdowns;
  if ($[0] !== t0) {
    ({
      errorMessage,
      showMonthYearDropdowns,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = errorMessage;
    $[2] = props;
    $[3] = showMonthYearDropdowns;
  } else {
    errorMessage = $[1];
    props = $[2];
    showMonthYearDropdowns = $[3];
  }
  const t = useTranslations("aria");
  let t1;
  if ($[4] !== t) {
    t1 = t("prev_month");
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <NavArrowLeft decorative={true} size="large" color="currentColor" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1) {
    t3 = <Button variant="icon" ariaLabel={t1} slot="previous">{t2}</Button>;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== showMonthYearDropdowns) {
    t4 = showMonthYearDropdowns ? <div className={styles.dropdownGroup}><MonthDropdown /><YearDropdown /></div> : <Heading />;
    $[9] = showMonthYearDropdowns;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("next_month");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <NavArrowRight decorative={true} size="large" color="currentColor" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5) {
    t7 = <Button variant="icon" ariaLabel={t5} slot="next">{t6}</Button>;
    $[14] = t5;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t7) {
    t8 = <header className={styles.header}>{t3}{t4}{t7}</header>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CalendarGrid weekdayStyle="short" className={styles["react-aria-CalendarGrid"]}><CalendarGridHeader>{_temp3}</CalendarGridHeader><CalendarGridBody className={styles["react-aria-CalendarGridBody"]}>{_temp4}</CalendarGridBody></CalendarGrid>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== errorMessage) {
    t10 = errorMessage && <Text slot="errorMessage">{errorMessage}</Text>;
    $[21] = errorMessage;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== props || $[24] !== t10 || $[25] !== t8) {
    t11 = <AriaCalendar data-test-id="Calendar" className={styles["react-aria-Calendar"]} {...props}>{t8}{t9}{t10}</AriaCalendar>;
    $[23] = props;
    $[24] = t10;
    $[25] = t8;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
export { Calendar };
function _temp3(day) {
  return <CalendarHeaderCell className={styles["react-aria-CalendarHeaderCell"]}>{day}</CalendarHeaderCell>;
}
function _temp4(date) {
  return <CalendarCell date={date} className={styles["react-aria-CalendarCell"]} />;
}